export interface StockSaveChunkResult {
  updatedCount: number;
  updatedStockCount: number;
  updatedParCount: number;
  unchangedCount: number;
  requestedCount: number;
  updatedStockLotIds: string[];
  updatedParCatalogItemIds: string[];
}

export interface StockSaveRow {
  stockLotId: string;
  updatedAt: string | null;
  parUpdatedAt: string | null;
  quantity: number;
  parQty: number;
  expirationDate: string | null;
  binLabel: string | null;
  notes: string;
}

export function isUnconfirmedStockMutationError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: unknown }).code === "MUTATION_UNCONFIRMED"
  );
}

export function isConfirmedStockSaveChunk(result: StockSaveChunkResult, expectedCount: number): boolean {
  return expectedCount > 0 && result.requestedCount === expectedCount;
}

export function removeConfirmedStockDrafts<T>(
  drafts: Record<string, T>,
  confirmedStockLotIds: Iterable<string>
): Record<string, T> {
  const next = { ...drafts };
  let changed = false;
  for (const stockLotId of confirmedStockLotIds) {
    if (!(stockLotId in next)) continue;
    delete next[stockLotId];
    changed = true;
  }
  return changed ? next : drafts;
}

export function applyConfirmedStockRows<
  T extends {
    stockLotId: string;
    catalogItemId: string;
    quantity: number;
    parQty: number;
    expirationDate: string | null;
    binLabel: string | null;
    notes: string | null;
  }
>(items: T[], rows: StockSaveRow[]): T[] {
  if (rows.length === 0) return items;
  const itemById = new Map(items.map((item) => [item.stockLotId, item]));
  const rowById = new Map(rows.map((row) => [row.stockLotId, row]));
  const parByCatalogItemId = new Map<string, number>();

  for (const row of rows) {
    const item = itemById.get(row.stockLotId);
    if (item) parByCatalogItemId.set(item.catalogItemId, row.parQty);
  }

  return items.map((item) => {
    const row = rowById.get(item.stockLotId);
    const parQty = parByCatalogItemId.get(item.catalogItemId) ?? item.parQty;
    if (!row && parQty === item.parQty) return item;
    return {
      ...item,
      ...(row
        ? {
            quantity: row.quantity,
            expirationDate: row.expirationDate,
            binLabel: row.binLabel,
            notes: row.notes
          }
        : {}),
      parQty
    };
  });
}

export function aggregateStockSaveResults(results: StockSaveChunkResult[]): StockSaveChunkResult {
  return results.reduce<StockSaveChunkResult>(
    (total, current) => ({
      updatedCount: total.updatedCount + current.updatedCount,
      updatedStockCount: total.updatedStockCount + current.updatedStockCount,
      updatedParCount: total.updatedParCount + current.updatedParCount,
      unchangedCount: total.unchangedCount + current.unchangedCount,
      requestedCount: total.requestedCount + current.requestedCount,
      updatedStockLotIds: [...total.updatedStockLotIds, ...current.updatedStockLotIds],
      updatedParCatalogItemIds: [
        ...total.updatedParCatalogItemIds,
        ...current.updatedParCatalogItemIds
      ]
    }),
    {
      updatedCount: 0,
      updatedStockCount: 0,
      updatedParCount: 0,
      unchangedCount: 0,
      requestedCount: 0,
      updatedStockLotIds: [],
      updatedParCatalogItemIds: []
    }
  );
}

export function partialStockSaveMessage(input: {
  confirmedCount: number;
  totalCount: number;
  refreshSucceeded: boolean;
  failureMessage: string;
}): string {
  const remainingCount = Math.max(0, input.totalCount - input.confirmedCount);
  const progress =
    input.confirmedCount > 0
      ? `Saved ${input.confirmedCount} of ${input.totalCount} stock changes. Up to ${remainingCount} ${
          remainingCount === 1 ? "change was" : "changes were"
        } unconfirmed.`
      : `The save was not confirmed. Up to ${input.totalCount} stock changes were unconfirmed.`;
  const reconciliation = input.refreshSucceeded
    ? "Stock was refreshed to reconcile anything the server received; only still-highlighted rows need to be retried."
    : "Stock could not be reconciled; keep this screen open and select Reconcile Stock when the connection improves.";
  return `${progress} ${reconciliation} Select Save Changes to retry remaining rows. ${input.failureMessage}`;
}
